import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { EmpReport } from "../types/EmpReport";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { validateEmployeeId } from "../config/functions";
import { StoreState } from "../redux/reducers";
import { fetchProjects } from "../redux/actions/projectAction";
import { fetchTasks } from "../redux/actions/taskAction";
import { fetchEmployeesAction } from "../redux/actions/employeeActions";
import "../styles/Form.css";

interface Props {
  reportIdToEdit?: number;
  onSave: (report: EmpReport) => void;
  initialData?: EmpReport | null;
}
const EmployeeReportForm: React.FC<Props> = ({
  reportIdToEdit,
  onSave,
  initialData,
}) => {
  const users = useSelector((state: StoreState) => state.authUser.employeeId);
  console.log(users);

  const initialState: EmpReport = {
    id: 0,
    employeeId: users,
    date: "",
    projectId: 0,
    taskId: 0,
    activity: "",
    hoursSpent: 0,
    comment: "",
    status: "Pending",
  };

  const [report, setReport] = useState<EmpReport>(initialData || initialState);
  const [project, setProject] = useState<{ id: number; name: string }>({
    id: 0,
    name: "",
  });
  const [task, setTask] = useState<{
    id: number;
    title: string;
    projectId: number;
  }>({ id: 0, title: "", projectId: 0 });
  const dispatch = useDispatch<any>();
  const projects = useSelector((state: StoreState) => state.project);
  const tasks = useSelector((state: StoreState) => state.task);

  type Task = { id: number; title: string; projectId: number };

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchTasks());
    dispatch(fetchEmployeesAction());
  }, [dispatch]);

  useEffect(() => {
    if (initialData && projects.length > 0 && tasks.length > 0) {
      setReport(initialData);

      const selectedProject = projects.find(
        (p) => p.id === initialData.projectId,
      );
      if (selectedProject)
        setProject({ id: selectedProject.id, name: selectedProject.name });

      const selectedTask = tasks.find((t) => t.id === initialData.taskId);
      if (selectedTask) {
        setTask({
          id: selectedTask.id,
          title: selectedTask.title,
          projectId: selectedTask.projectId,
        });
      }
    }
  }, [initialData, projects, tasks]);

  useEffect(() => {
    if (!initialData && users) {
      setReport((prev) => ({
        ...prev,
        employeeId: users,
      }));
    }
  }, [users, initialData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    if (name === "hoursSpent") {
      if (/^\d*\.?\d*$/.test(value)) {
        let cleanValue = value;
        if (cleanValue.startsWith("0") && !cleanValue.startsWith("0.")) {
          cleanValue = cleanValue.replace(/^0+/, "");
        }
        setReport((prev) => ({
          ...prev,
          hoursSpent: cleanValue,
        }));
      }
    } else {
      setReport((prev) => ({ ...prev, [name]: value }));
    }
  };

  // const handleSubmit = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   onSave(report);
  // };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSave(report);
    } catch (error) {
      console.error("Failed to save report:", error);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-1">
        <Col md={4} className="form-column">
          <Form.Group controlId="date">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={report.date}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>
        <Col md={4} className="form-column">
          <Form.Group controlId="ProjectId">
            <Form.Label>
              Project <span className="text-danger">*</span>
            </Form.Label>
            <Select
              options={projects.map((p) => ({
                label: p.name,
                value: p.id.toString(),
              }))}
              value={
                project.id
                  ? {
                      label: project.name,
                      value: project.id.toString(),
                    }
                  : null
              }
              placeholder="Select Project"
              isMulti={false}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  const id = parseInt(selectedOption?.value || "0");
                  const selectedProject =
                    projects.find((p) => p.id === id)?.name || "";

                  setProject({ id, name: selectedProject });

                  setReport((prev) => ({
                    ...prev,
                    projectId: id,
                    taskId: 0,
                  }));

                  setTask({ id: 0, title: "", projectId: id });
                } else {
                  setProject({ id: 0, name: "" });
                  setReport((prev) => ({ ...prev, projectId: 0, taskId: 0 }));
                  setTask({ id: 0, title: "", projectId: 0 });
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="form-column">
          <Form.Group controlId="taskId">
            <Form.Label>Task</Form.Label>
            <Select
              isDisabled={project.id === 0}
              options={tasks
                .filter((t) => t.projectId === project.id)
                .map((task: Task) => ({
                  label: task.title,
                  value: task.id.toString(),
                }))}
              placeholder={
                project.id === 0 ? "Select Project First" : "Select Task"
              }
              isMulti={false}
              value={
                task.id
                  ? {
                      label: task.title,
                      value: task.id.toString(),
                    }
                  : null
              }
              onChange={(selectedOption) => {
                const id = parseInt(selectedOption?.value || "0");
                const selectedTask = tasks.find((t) => t.id === id);
                if (selectedTask) {
                  setTask({
                    id,
                    title: selectedTask.title,
                    projectId: selectedTask.projectId,
                  });

                  setReport((prev) => ({
                    ...prev,
                    taskId: id,
                  }));
                }
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-1">
        <Col md={4} className="form-column">
          <Form.Group controlId="activity">
            <Form.Label>Activity</Form.Label>
            <Form.Select
              name="activity"
              value={report.activity}
              onChange={handleChange}
              required
            >
              <option value="">select Options</option>
              <option value="Development">Development</option>
              <option value="Design">Design</option>
              <option value="Testing">Testing</option>
              <option value="DataEntry">DataEntry</option>
              <option value="Documentation">Documentation</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} className="form-column">
          <Form.Group controlId="hoursSpent">
            <Form.Label>Hours Spent</Form.Label>
            <Form.Control
              type="text"
              name="hoursSpent"
              value={report.hoursSpent || ""}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>
        <Col md={4} className="form-column">
          <Form.Group controlId="comment">
            <Form.Label>Comment</Form.Label>
            <Form.Control
              as="textarea"
              name="comment"
              value={report.comment}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-1">
        {/* <Form.Group controlId="status">
            <Form.Label>Status</Form.Label>
        <Form.Select
          name="status"
          value={report.status}
          onChange={handleChange}
        >
          <option value="Pending">Pending</option>
          <option value="Completed">Completed</option>
        </Form.Select>
      </Form.Group> */}

        <Col></Col>
        <Col className="form-column">
          <div className="form-buttons">
            <Button type="submit" className="btn-save">
              {reportIdToEdit ? "Update" : "Save"}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default EmployeeReportForm;
